import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { getProjectMembers, type ProjectMember } from '../transport/api'
import { chatv1SelfUser } from '../chatv1/auth'
import { Chatv1Error, type EditMode } from '../chatv1/transport'
import './board.css'

/**
 * WHO MAY CHANGE A BOARD OR A PAGE — owner, 2026-11-02: 白板還有Pages這兩種使用者可以協作共用的這種方式需要在上面加上一層lock
 * … 原本擁有的使用者可以指定要讓他分享給開放給哪些使用者可以來修改或者是不要開放它被其他的使用者來修改.
 *
 * The rule is the service's (chat lambda/elements.ts): no lock, everyone in the room; `owner`, its owner alone; `some`,
 * its owner and the people chosen. Its owner is who made it (`by`); a room's `main` board, made by nobody, is the room
 * owner's. The owner sets it, and so may the room's owner. Everyone in the room can still see it, and an agent can
 * change it when the person who attached it can. What is here only says so: the service refuses whatever it says no to.
 */

export type Lockable = { by?: string; edit?: 'owner' | 'some'; editors?: string[] }

/** Whether this person may change it. Not known yet (the list on its way): the service still decides. */
export function mayChange(info: Lockable | null | undefined, me: string): boolean {
  return !info?.edit || !!info.editors?.includes(me)
}

/** The people of the room a lock can name — the agents go by the person who attached them. */
function peopleOf(members: ProjectMember[]): ProjectMember[] {
  return members.filter((m) => m.kind !== 'agent' && !!m.user)
}

const nameOf = (m: ProjectMember | undefined): string => m?.label ?? m?.email ?? 'its owner'

function failText(e: unknown): string {
  const code = e instanceof Chatv1Error ? e.code : ''
  if (code === 'not_the_owner') return 'Only its owner or the project owner can change this.'
  if (code === 'not_a_member') return 'You are no longer in this project.'
  return 'That did not save — check the connection and try again.'
}

/**
 * In the header of a board or a page. Who may set the lock has a button that says who can edit, and opens the choice;
 * anyone else sees only what holds for them, and only when it is locked: "View only", or a lock when they are among
 * those who may.
 */
export function EditLock({
  convId,
  what,
  info,
  onSave,
  className,
}: {
  convId: string
  what: 'board' | 'page'
  info: Lockable | null | undefined
  /** Sets the lock, and puts the answer into the list. */
  onSave: (edit: EditMode, editors?: string[]) => Promise<void>
  className?: string
}) {
  const me = chatv1SelfUser()
  const membersQ = useQuery({ queryKey: ['proj-members', convId], queryFn: () => getProjectMembers(convId) })
  const people = peopleOf(membersQ.data?.members ?? [])
  const roomOwner = people.find((m) => m.role === 'owner')?.user
  const owner = info?.by ?? roomOwner
  const maySet = !!info && !!me && (me === owner || me === roomOwner)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<React.CSSProperties>({})
  const whose = people.find((m) => m.user === owner)
  const mine = owner === me
  const mode: EditMode = info?.edit ?? 'all'
  const chosen = new Set((info?.editors ?? []).filter((u) => u !== owner))

  useLayoutEffect(() => {
    if (!open) return
    const r = btn.current?.getBoundingClientRect()
    if (!r) return
    const width = Math.min(300, window.innerWidth - 16)
    setPos({ width, top: r.bottom + 6, left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)) })
  }, [open])

  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => {
      const t = e.target as Node
      if (box.current?.contains(t) || btn.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('pointerdown', down, true)
    return () => window.removeEventListener('pointerdown', down, true)
  }, [open])

  if (!maySet) {
    if (!info?.edit) return null
    const may = mayChange(info, me)
    const who = info.edit === 'owner' ? `Only ${nameOf(whose)} can change this ${what}.` : `Only the people ${nameOf(whose)} chose can change this ${what}.`
    return (
      <span className={`edit-lock-chip${className ? ` ${className}` : ''}`} title={may ? `You can change this ${what}; others in the project can only view it.` : who} role="status">
        <Icon name="lock" size={14} />
        {!may && <span>View only</span>}
      </span>
    )
  }

  const save = (edit: EditMode, editors?: string[]) => {
    setBusy(true)
    setErr(null)
    onSave(edit, editors)
      .catch((e) => setErr(failText(e)))
      .finally(() => setBusy(false))
  }
  const toggle = (user: string) => {
    const next = new Set(chosen)
    if (next.has(user)) next.delete(user)
    else next.add(user)
    save('some', [...next])
  }
  const others = people.filter((m) => m.user !== owner)
  const label = mode === 'all' ? 'Everyone can edit' : mode === 'owner' ? (mine ? 'Only you can edit' : `Only ${nameOf(whose)} can edit`) : `${chosen.size + 1} can edit`
  const row = (value: EditMode, title: string, note: string) => (
    <label className={`edit-lock-mode${mode === value ? ' on' : ''}`}>
      <input type="radio" name={`edit-lock-${what}`} checked={mode === value} disabled={busy} onChange={() => save(value, value === 'some' ? [...chosen] : undefined)} />
      <span>
        <strong>{title}</strong>
        <small>{note}</small>
      </span>
    </label>
  )
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`edit-lock-btn${mode === 'all' ? '' : ' locked'}${open ? ' on' : ''}${className ? ` ${className}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Who can edit this ${what}: ${label}`}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={mode === 'all' ? 'group' : 'lock'} size={16} />
        <span className="edit-lock-label">{label}</span>
      </button>
      {open && (
        <div
          ref={box}
          className="edit-lock-box"
          style={pos}
          role="dialog"
          aria-label={`Who can edit this ${what}`}
          // Its own: the board's keys, and the drawer's pull on the header it sits in, are not for what happens in here.
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Escape') setOpen(false)
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="edit-lock-head">Who can edit this {what}</div>
          {row('all', 'Everyone in this project', 'Every person here, and their agents.')}
          {row('owner', mine ? 'Only me' : `Only ${nameOf(whose)}`, mine ? 'And my agents.' : 'And the agents of that person.')}
          {row('some', mine ? 'Me and people I choose' : `${nameOf(whose)} and people chosen`, 'And the agents of those people.')}
          {mode === 'some' &&
            (others.length ? (
              <ul className="edit-lock-people">
                {others.map((m) => (
                  <li key={m.user}>
                    <label>
                      <input type="checkbox" checked={chosen.has(m.user!)} disabled={busy} onChange={() => toggle(m.user!)} />
                      <span>{m.user === me ? 'Me' : nameOf(m)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="edit-lock-note">Nobody else is in this project yet.</p>
            ))}
          <p className="edit-lock-note">Everyone in the project can still see it.</p>
          {err && (
            <p className="edit-lock-err" role="alert">
              {err}
            </p>
          )}
        </div>
      )}
    </>
  )
}
